import { Controller } from "@hotwired/stimulus"
import { leadDrill } from "../lead_drill"

/**
 * Google Charts GeoChart wrapper.
 *
 * Attached to a container <div> with:
 *   data-controller="geo-chart"
 *   data-geo-chart-region-value="US"               (default: "world")
 *   data-geo-chart-resolution-value="provinces"    (default: "countries")
 *   data-geo-chart-url-value="<json endpoint>"
 *   data-geo-chart-download-filename-value="..."   (reserved for Phase 2)
 *   data-geo-chart-bins-value='{"bins":[5,20,75,200,null], "colors":[...]}'
 *                                                  (optional — driven by
 *                                                  GeoPaletteHelper#geo_chart_bins_payload)
 *   data-geo-chart-drilldown-frame-value="..."     (optional — DOM id of
 *                                                  the Turbo Frame that
 *                                                  hosts the city drill-down
 *                                                  panel; click/select on a
 *                                                  region triggers a Turbo
 *                                                  visit to load it)
 *   data-geo-chart-drilldown-url-value="..."       (optional — base URL
 *                                                  with `state=` appended)
 *   data-geo-chart-value-label-value="..."         (optional — header for
 *                                                  the value column;
 *                                                  default "Leads".
 *                                                  Override when the
 *                                                  payload values aren't
 *                                                  literal lead counts —
 *                                                  e.g. Territory Coverage
 *                                                  Gap ships sentinel
 *                                                  bucket IDs.)
 *   data-geo-chart-tooltips-value='{...}'          (optional — JSON object
 *                                                  mapping region label →
 *                                                  custom tooltip string.
 *                                                  When present, a third
 *                                                  DataTable column with
 *                                                  `role: "tooltip"` is
 *                                                  emitted so Google
 *                                                  geo_chart shows the
 *                                                  category-aware copy
 *                                                  on hover instead of
 *                                                  "<region> — <header>:
 *                                                  <sentinel>".)
 *
 * Endpoint shape:
 *   ExhibitorChartsController#leads_by_states and #leads_by_country render
 *   a Ruby hash as JSON: { "California": 42, "Texas": 30 }
 *   We transform this into a Google Charts data table via arrayToDataTable.
 *
 * Palette:
 *   When `bins-value` is provided we render a discrete-bin choropleth using
 *   the ColorBrewer YlGnBu palette computed server-side. Without bins we
 *   fall back to the legacy 2-stop continuous gradient so any not-yet-
 *   migrated callers keep working.
 *
 * Fallback UX: if Google Charts fails to load (offline, CSP) or the fetch
 * errors, we swap the element for a neutral empty-state so the page
 * doesn't show a broken chart.
 */

let googleLoaderPromise = null

function loadGoogleCharts() {
  if (googleLoaderPromise) return googleLoaderPromise

  googleLoaderPromise = new Promise((resolve, reject) => {
    const resolveWhenReady = () => {
      try {
        window.google.charts.load("current", { packages: ["geochart"], mapsApiKey: "" })
        window.google.charts.setOnLoadCallback(resolve)
      } catch (err) {
        reject(err)
      }
    }

    if (window.google && window.google.charts) {
      resolveWhenReady()
      return
    }

    const script = document.createElement("script")
    script.src = "https://www.gstatic.com/charts/loader.js"
    script.async = true
    script.onload = resolveWhenReady
    script.onerror = () => reject(new Error("Failed to load Google Charts loader"))
    document.head.appendChild(script)
  })

  return googleLoaderPromise
}

export default class extends Controller {
  static values = {
    region:             { type: String, default: "world" },
    resolution:         { type: String, default: "countries" },
    url:                { type: String, default: "" },
    downloadFilename:   { type: String, default: "" },
    bins:               { type: Object, default: {} },
    drilldownFrame:     { type: String, default: "" },
    drilldownUrl:       { type: String, default: "" },
    // valueLabel — column-2 header. Default "Leads" preserves the legacy
    // tooltip ("California — Leads: 42") for every existing caller. Override
    // when the column-2 value is not a literal lead count (e.g. Territory
    // Coverage Gap ships sentinel bucket IDs 1..4).
    valueLabel:         { type: String, default: "Leads" },
    // tooltips — { regionLabel: tooltipString } map. Presence triggers a
    // third DataTable column with `role: "tooltip"` so Google overrides
    // its default tooltip text. Absence preserves the legacy 2-column
    // shape so other callers keep working.
    tooltips:           { type: Object, default: {} }
  }

  async connect() {
    this._aborted = false

    try {
      await loadGoogleCharts()
      if (this._aborted) return

      const payload = await this._fetchData()
      if (this._aborted) return

      this._lastPayload = payload
      this._draw(payload)
      this._connected = true
    } catch (err) {
      console.warn("geo-chart failed:", err)
      this._renderEmptyState("Map unavailable.")
    }
  }

  // Re-fetch + re-draw when the URL changes (e.g. metric toggle on the
  // Lead Quality by Geography card swaps from count → quality data).
  // Stimulus invokes this callback once when the controller is connecting
  // (before our connect() runs) — guard with `_connected` so we only act
  // on real subsequent mutations.
  async urlValueChanged() {
    if (!this._connected || this._aborted) return
    // LRPO-5570: never repaint into a collapsed container. When the card is in
    // its data-table view the map is display:none (d-none) and measures 0px
    // wide; Google GeoChart would then re-draw a shrunken SVG that surfaces
    // when the user toggles back. Skip the redraw while hidden — the map keeps
    // its correctly-sized SVG, and a later change while visible repaints it.
    if (!this._isVisible()) return
    try {
      const payload = await this._fetchData()
      if (this._aborted) return
      this._lastPayload = payload
      this._draw(payload)
    } catch (err) {
      console.warn("geo-chart url change failed:", err)
    }
  }

  // Redraw with the same payload when the bins config changes (metric
  // toggle swaps the bins payload alongside the URL). Cheap re-render —
  // no network round trip.
  binsValueChanged() {
    if (!this._connected || !this._lastPayload || this._aborted) return
    // LRPO-5570: same collapsed-container guard as urlValueChanged — a redraw
    // into a 0-width d-none container shrinks the map.
    if (!this._isVisible()) return
    this._draw(this._lastPayload)
  }

  // True when the chart container is actually laid out (visible). A d-none
  // ancestor (the table-view toggle) or a detached node collapses
  // `offsetParent` to null, which is exactly the "don't redraw" signal we
  // want. Guarded so a mid-toggle value change never repaints a 0-width map.
  _isVisible() {
    return this.element != null && this.element.offsetParent !== null
  }

  disconnect() {
    this._aborted = true
    // Clear any rendered map so a cached page doesn't keep a stale SVG.
    this._clearContainer()
  }

  async _fetchData() {
    if (!this.urlValue) return {}
    const response = await fetch(this.urlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    if (!response.ok) throw new Error(`GeoChart fetch failed: ${response.status}`)
    return response.json()
  }

  _draw(payload) {
    const rows = this._toRows(payload)
    if (rows.length <= 1) {
      this._renderEmptyState("No geographic data yet.")
      return
    }

    // Reset container so we don't stack on top of Chartkick's fallback.
    this._clearContainer()

    const data = window.google.visualization.arrayToDataTable(rows)
    const chart = new window.google.visualization.GeoChart(this.element)

    const colorAxis = this._colorAxis(rows.slice(1))

    chart.draw(data, {
      region: this.regionValue,
      resolution: this.resolutionValue,
      colorAxis,
      backgroundColor: "transparent",
      datalessRegionColor: this._zeroFill(),
      defaultColor: this._zeroFill(),
      legend: this._legendConfig(),
      tooltip: { textStyle: { color: "#212529", fontSize: 12 } }
    })

    // A single `select` listener serves two drill behaviors:
    //   1. Legacy frame drilldown — loads a city-level panel into a Turbo
    //      Frame (drilldownUrl + drilldownFrame values).
    //   2. leadDrill — when the host element carries `data-drill`, a
    //      page-level Turbo visit to /exhibitor/leads filtered by `state`.
    // Both can be active; the `data-drill` branch is dormant until a
    // partial opts in.
    const hasFrameDrill = this.drilldownUrlValue && this.drilldownFrameValue
    const hasLeadDrill  = !!this.element.dataset.drill
    if (hasFrameDrill || hasLeadDrill) {
      window.google.visualization.events.addListener(chart, "select", () => {
        const sel = chart.getSelection()
        if (!sel || !sel.length) return
        const row = sel[0].row
        if (row == null) return
        const regionLabel = data.getValue(row, 0)
        if (!regionLabel) return
        if (hasFrameDrill) this._triggerDrilldown(regionLabel)
        if (hasLeadDrill)  this._leadDrillForRegion(regionLabel)
      })
    }
  }

  // leadDrill branch of the GeoChart `select` handler. Builds a flat
  // /exhibitor/leads URL with the selected region as `state` (param key
  // overridable via the data-drill config). The host element carries:
  //   data-drill = { origin, anchor, param?, filters? }
  // `param` defaults to "state". `filters` is an optional object of fixed
  // extra filter params merged into every drill from this chart — used by
  // the Daily Spread small-multiples, where each mini-map represents one
  // event day, so the day's scan_date_exact is a constant per chart.
  _leadDrillForRegion(regionLabel) {
    let cfg
    try {
      cfg = JSON.parse(this.element.dataset.drill)
    } catch (e) {
      console.warn("geo-chart: data-drill is not valid JSON:", e.message)
      return
    }
    const param = cfg.param || "state"
    const fixed = (cfg.filters && typeof cfg.filters === "object") ? cfg.filters : {}
    leadDrill(
      { ...fixed, [param]: regionLabel },
      { origin: cfg.origin, anchor: cfg.anchor }
    )
  }

  // ColorBrewer YlGnBu 6-class palette — kept inline so the fallback
  // gradient stays vibrant even when the server-side bin payload is
  // empty. Mirrors GeoPaletteHelper::YLGNBU_6 in Ruby.
  static YLGNBU_6 = ["#ffffd9", "#c7e9b4", "#7fcdbb", "#41b6c4", "#2c7fb8", "#253494"]

  _colorAxis(rows) {
    if (this._hasBins()) {
      const bins   = Array.isArray(this.binsValue.bins)   ? this.binsValue.bins   : []
      const colors = Array.isArray(this.binsValue.colors) ? this.binsValue.colors : []

      // Sanitize bins: drop nullish (null = ∞ sentinel handled below),
      // coerce to numbers, drop NaN, and remember which slot was the
      // open-ended sentinel so we can replace it with the data max.
      const sanitized = []
      bins.forEach((upper, i) => {
        const isOpenEnded = upper == null
        const num = isOpenEnded ? this._maxOf(rows) : Number(upper)
        if (!Number.isFinite(num)) return
        const color = colors[i] || colors[colors.length - 1] || "#295D77"
        sanitized.push({ upper: num, color, isOpenEnded })
      })

      if (sanitized.length === 0) {
        return this._fullYlGnBuGradient(rows)
      }

      // Pick an epsilon that's small relative to the data range so adjacent
      // (lower, upper) stops stay strictly increasing without visibly
      // shifting the bin boundary. For integer count data the range is
      // typically dozens-to-hundreds; for fractional avg-quality data the
      // range is sub-1.0. A scale-aware epsilon prevents the historic bug
      // where the seed lower-bound `1` was greater than a fractional first
      // bin upper (e.g. `0.5`) — non-monotonic, Google rejects it.
      const dataMin = this._minPositive(rows)
      const dataMax = sanitized[sanitized.length - 1].upper
      const span = Math.max(dataMax - 0, 0)
      const epsilon = Math.max(span * 1e-6, Number.EPSILON * 16)

      // Build colorAxis values/colors arrays. Google geo_chart paints a
      // continuous gradient between adjacent stops; to get visually
      // discrete bins we anchor each color to its bin's upper bound and
      // re-anchor the next bin's lower bound just above the previous
      // upper — this collapses the gradient into flat plateaus while
      // staying strictly monotonic.
      const values = []
      const colorStops = []
      let prev = null
      sanitized.forEach(({ upper, color }, i) => {
        // First bin's lower bound seeds below the bin's upper bound so the
        // (lower, upper) pair is strictly increasing. We pick the smaller
        // of (data minimum, upper - epsilon) so the gradient still starts
        // close to real data when the data is dense.
        let lowerSeed
        if (i === 0) {
          const candidate = dataMin > 0 && dataMin < upper ? dataMin : upper - epsilon
          lowerSeed = Number.isFinite(candidate) ? candidate : upper - epsilon
        } else {
          lowerSeed = prev + epsilon
        }
        // Guard: if rounding/precision pushed lower past upper, nudge upper.
        const safeUpper = upper > lowerSeed ? upper : lowerSeed + epsilon
        values.push(lowerSeed)
        colorStops.push(color)
        values.push(safeUpper)
        colorStops.push(color)
        prev = safeUpper
      })

      // Belt-and-suspenders: if the values array somehow ends up
      // non-monotonic (NaN, dup, or precision collapse) fall back to
      // the FULL YlGnBu 6-stop gradient over the data span. The map
      // stays useful — the user still sees gradient differentiation
      // across states — even though we lose discrete-bin shading.
      if (!this._isStrictlyIncreasing(values)) {
        console.warn("geo-chart: non-monotonic colorAxis values, falling back to full YlGnBu gradient", values)
        return this._fullYlGnBuGradient(rows)
      }

      return {
        values,
        colors: colorStops,
        minValue: values[0]
      }
    }

    // No bins payload — render the full YlGnBu gradient over the data
    // span so callers that don't ship discrete bins still get a vibrant
    // choropleth (instead of the legacy two-stop teal-on-gray gradient).
    return this._fullYlGnBuGradient(rows)
  }

  // Build a 6-stop YlGnBu colorAxis spanning [dataMin, dataMax] with
  // even spacing — used when discrete bins aren't available. Guarantees
  // strict monotonicity by construction.
  _fullYlGnBuGradient(rows) {
    const dataMin = this._minPositive(rows)
    const dataMax = this._maxOf(rows)
    const palette = this.constructor.YLGNBU_6

    if (!Number.isFinite(dataMax) || dataMax <= 0) {
      // No usable data — return the lightest two stops so unmapped
      // regions still render via datalessRegionColor.
      return { colors: [palette[0], palette[palette.length - 1]] }
    }

    const lo = dataMin > 0 && dataMin < dataMax ? dataMin : Math.max(dataMax * 0.0001, Number.EPSILON * 16)
    const hi = dataMax > lo ? dataMax : lo + Math.max(lo * 1e-6, Number.EPSILON * 16)
    const steps = palette.length
    const span = hi - lo
    const values = []
    for (let i = 0; i < steps; i++) {
      values.push(lo + (span * i) / (steps - 1))
    }
    // Final monotonic guard — pathological data could still produce
    // adjacent collapses; nudge if needed so Google never throws.
    for (let i = 1; i < values.length; i++) {
      if (values[i] <= values[i - 1]) {
        values[i] = values[i - 1] + Math.max(Math.abs(values[i - 1]) * 1e-6, Number.EPSILON * 16)
      }
    }

    return { values, colors: palette.slice(), minValue: values[0] }
  }

  _isStrictlyIncreasing(values) {
    for (let i = 1; i < values.length; i++) {
      if (!Number.isFinite(values[i]) || !Number.isFinite(values[i - 1])) return false
      if (values[i] <= values[i - 1]) return false
    }
    return values.length >= 2
  }

  _minPositive(rows) {
    let min = Infinity
    rows.forEach(([, v]) => {
      const n = Number(v)
      if (Number.isFinite(n) && n > 0 && n < min) min = n
    })
    return Number.isFinite(min) ? min : 0
  }

  _legendConfig() {
    // Always suppress Google's default gradient bar — we render our own
    // custom discrete-swatch legend (or, when bins are degenerate, the
    // gradient still stays visually consistent with the chart body and
    // doesn't need a legend at all). Either way, the ERB template owns
    // the on-screen legend.
    return "none"
  }

  _hasBins() {
    return this.binsValue && Array.isArray(this.binsValue.bins) && this.binsValue.bins.length > 0
  }

  _zeroFill() {
    return (this.binsValue && this.binsValue.zero) || "#f5f5f5"
  }

  _maxOf(rows) {
    let max = 0
    rows.forEach(([, v]) => { if (Number(v) > max) max = Number(v) })
    return max || 1
  }

  _triggerDrilldown(regionLabel) {
    // Region label may be a US state name ("North Carolina"). Pass through
    // as-is — the controller does the name → code mapping on the server.
    const url = `${this.drilldownUrlValue}${this.drilldownUrlValue.includes("?") ? "&" : "?"}state=${encodeURIComponent(regionLabel)}`
    const frame = document.getElementById(this.drilldownFrameValue)
    if (!frame) {
      console.warn(`geo-chart drill-down frame "${this.drilldownFrameValue}" not found`)
      return
    }
    frame.src = url
  }

  _toRows(payload) {
    // Charts controller endpoints return hashes like { "California": 42 }.
    // Google Charts wants a 2-D array with a header row.
    //
    // When `tooltipsValue` is supplied, we add a 3rd column with
    // `role: "tooltip"` so Google overrides its default tooltip text
    // ("<region> — <header>: <value>") with the per-region category copy
    // the controller built. This is what fixes the Territory Coverage Gap
    // bug where the sentinel-based payload made the default tooltip
    // ("Wyoming — Leads: 3") read as nonsense to the user.
    const valueLabel = (typeof this.valueLabelValue === "string" && this.valueLabelValue.length > 0)
      ? this.valueLabelValue
      : "Leads"
    const hasTooltips = this._hasTooltips()
    const tooltipMap = hasTooltips ? this.tooltipsValue : null

    const header = hasTooltips
      ? ["Location", valueLabel, { type: "string", role: "tooltip" }]
      : ["Location", valueLabel]

    if (!payload) return [header]

    let entries
    if (Array.isArray(payload)) {
      entries = payload
        .filter(e => Array.isArray(e) && e.length >= 2)
        .map(([loc, count]) => [loc, Number(count) || 0])
    } else if (typeof payload === "object") {
      entries = Object.entries(payload).map(([loc, count]) => [loc, Number(count) || 0])
    } else {
      entries = []
    }

    if (hasTooltips) {
      // Default to "" (not undefined) when a region is missing from the
      // override map so the DataTable shape stays well-formed and Google
      // doesn't fall back to its synthesized "<region> — <header>: <value>"
      // text on a per-row basis.
      entries = entries.map(([loc, count]) => [loc, count, tooltipMap[loc] || ""])
    }

    return [header, ...entries]
  }

  _hasTooltips() {
    return this.tooltipsValue
      && typeof this.tooltipsValue === "object"
      && !Array.isArray(this.tooltipsValue)
      && Object.keys(this.tooltipsValue).length > 0
  }

  _clearContainer() {
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild)
    }
  }

  _renderEmptyState(message) {
    // Build the empty-state DOM without innerHTML to avoid any XSS surface.
    this._clearContainer()

    const wrapper = document.createElement("div")
    wrapper.className = "report-empty-state text-center text-muted py-4"

    const p = document.createElement("p")
    p.className = "report-empty-state__description mb-0"
    p.textContent = message

    wrapper.appendChild(p)
    this.element.appendChild(wrapper)
  }
}
